import { MovableDialog } from '@/components/shared/MovableDialog';
import { useState, type ReactElement } from 'react';
import { v2, parseApiErrorBody } from '@/api/v2Client';

/**
 * Phase UI-13 — Unlock sequence button + confirmation modal.
 *
 * Renders next to the toolbar when the submission is Complete /
 * Published. On click, prompts "This will unlock the submission for
 * editing. Are you sure?". On confirm, PATCHes Status=Draft. The
 * server rejects with 409 if a higher SequenceNumber exists.
 */

export interface UnlockSequenceButtonProps {
  readonly submissionId: string;
  readonly currentStatus: string | null;
  readonly onUnlocked?: () => void;
  readonly canUnlock: boolean;
}



export function UnlockSequenceButton({
  submissionId,
  currentStatus,
  onUnlocked,
  canUnlock,
}: UnlockSequenceButtonProps): ReactElement | null {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  if (!canUnlock || (currentStatus !== 'Complete' && currentStatus !== 'Published')) return null;

  const onConfirm = async (): Promise<void> => {
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await v2.submissions.unlock(submissionId, reason.trim());
      setConfirmOpen(false);
      if (onUnlocked) onUnlocked();
    } catch (err) {
      const body = parseApiErrorBody(err);
      setError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="unlock-btn"
        onClick={() => {
          setError(null);
          setConfirmOpen(true);
        }}
        style={{
          padding: '6px 14px',
          background: 'var(--surface2)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        Unlock sequence
      </button>
      {confirmOpen && (
        /* phase-movable-dialogs — shared movable shell. */
        <MovableDialog
          persistKey="compiler.unlockConfirm"
          title="Unlock submission?"
          testId="unlock-confirm-modal"
          onClose={busy ? undefined : () => setConfirmOpen(false)}
          escDisabled={busy}
          width={420}
          zIndex={1000}
        >
          <>
            <p style={{ margin: 0, fontSize: 13 }}>
              Give a reason for this controlled editing exception. The published
              package and submission history are retained.
            </p>
            <label>
              Reason for unlocking
              <textarea aria-label="Reason for unlocking" required value={reason}
                onChange={(event) => setReason(event.target.value)} disabled={busy} />
            </label>
            {error && (
              <div
                role="alert"
                data-testid="unlock-error"
                style={{ color: 'var(--danger, #b91c1c)', fontSize: 12 }}
              >
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="unlock-cancel"
                disabled={busy}
                onClick={() => setConfirmOpen(false)}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: 'var(--surface)',
                  cursor: busy ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="unlock-confirm-yes"
                disabled={busy || !reason.trim()}
                onClick={() => void onConfirm()}
                style={{
                  padding: '6px 14px',
                  background: 'var(--accent, #4f46e5)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: busy ? 'not-allowed' : 'pointer',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                {busy ? 'Unlocking…' : 'Unlock'}
              </button>
            </div>
          </>
        </MovableDialog>
      )}
    </>
  );
}
